import { create } from 'zustand';
import { WebSocketMessage, WebSocketStatus } from '@/hooks/useWebSocket';
import { logger } from '@/utils/logger';

// TTL for messages in milliseconds (5 minutes)
const MESSAGE_TTL_MS = 5 * 60 * 1000;
const MAX_QUEUE_SIZE = 100;
const MAX_OUTGOING_QUEUE_SIZE = 50;

// Message with timestamp for TTL tracking
interface TimestampedMessage {
  message: WebSocketMessage;
  timestamp: number;
  id: string; // Unique message ID for deduplication and replay tracking
}

// Outgoing message queue item
interface QueuedOutgoingMessage {
  id: string;
  message: any;
  timestamp: number;
  retryCount: number;
}

// Connection health metrics
interface ConnectionHealth {
  lastPingTime: number | null;
  lastPongTime: number | null;
  latency: number | null;
  missedPongs: number;
  isHealthy: boolean;
}

// Reconnection state
interface ReconnectionState {
  attempts: number;
  maxAttempts: number;
  nextRetryTime: number | null;
  lastDisconnectTime: number | null;
  lastMessageId: string | null; // For replay tracking
}

interface RealtimeState {
  status: WebSocketStatus;
  lastMessage: WebSocketMessage | null;
  messageQueue: TimestampedMessage[];
  outgoingQueue: QueuedOutgoingMessage[];
  subscriptions: Map<string, Set<(message: WebSocketMessage) => void>>;
  connectionHealth: ConnectionHealth;
  reconnectionState: ReconnectionState;
  statusMessage: string;

  // Actions
  setStatus: (status: WebSocketStatus, message?: string) => void;
  addMessage: (message: WebSocketMessage) => void;
  subscribe: (channel: string, callback: (message: WebSocketMessage) => void) => () => void;
  clearMessages: () => void;
  getMessages: () => WebSocketMessage[];

  // Outgoing queue management
  queueOutgoingMessage: (message: any) => string;
  getOutgoingQueue: () => QueuedOutgoingMessage[];
  clearOutgoingQueue: () => void;
  removeFromOutgoingQueue: (id: string) => void;
  markMessageSent: (id: string) => void;

  // Connection health
  updatePingTime: () => void;
  updatePongTime: () => void;
  incrementMissedPongs: () => void;
  resetConnectionHealth: () => void;
  getConnectionHealth: () => ConnectionHealth;

  // Reconnection state
  updateReconnectionState: (updates: Partial<ReconnectionState>) => void;
  resetReconnectionState: () => void;
  getReconnectionState: () => ReconnectionState;

  // Message replay support
  getMessagesAfter: (messageId: string | null) => WebSocketMessage[];
  setLastMessageId: (id: string) => void;
}

// Generate unique message ID
const generateMessageId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

// Initial connection health state
const initialConnectionHealth: ConnectionHealth = {
  lastPingTime: null,
  lastPongTime: null,
  latency: null,
  missedPongs: 0,
  isHealthy: true,
};

// Initial reconnection state
const initialReconnectionState: ReconnectionState = {
  attempts: 0,
  maxAttempts: 10,
  nextRetryTime: null,
  lastDisconnectTime: null,
  lastMessageId: null,
};

// Get status message based on state
const getStatusMessage = (status: WebSocketStatus, reconnectionState: ReconnectionState): string => {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'connecting':
      if (reconnectionState.attempts > 0) {
        return `Reconnecting (attempt ${reconnectionState.attempts}/${reconnectionState.maxAttempts})...`;
      }
      return 'Connecting...';
    case 'disconnecting':
      return 'Disconnecting...';
    case 'disconnected':
      if (reconnectionState.attempts >= reconnectionState.maxAttempts) {
        return 'Connection failed - max attempts reached';
      }
      return 'Disconnected';
    case 'error':
      return 'Connection error';
    default:
      return 'Unknown status';
  }
};

export const useRealtimeStore = create<RealtimeState>((set, get) => ({
  status: 'disconnected',
  lastMessage: null,
  messageQueue: [],
  outgoingQueue: [],
  subscriptions: new Map(),
  connectionHealth: { ...initialConnectionHealth },
  reconnectionState: { ...initialReconnectionState },
  statusMessage: 'Disconnected',

  setStatus: (status, message) => {
    const { reconnectionState } = get();
    const statusMessage = message || getStatusMessage(status, reconnectionState);
    set({ status, statusMessage });
  },

  addMessage: (message) => {
    const now = Date.now();
    const messageId = (message as any).id || generateMessageId();
    const timestampedMessage: TimestampedMessage = {
      message,
      timestamp: now,
      id: messageId,
    };

    set((state) => {
      // Filter out expired messages (older than TTL)
      const validMessages = state.messageQueue.filter(
        (item) => now - item.timestamp < MESSAGE_TTL_MS
      );

      // Check for duplicate messages (by ID)
      const isDuplicate = validMessages.some((item) => item.id === messageId);
      if (isDuplicate) {
        logger.debug('Duplicate message received, skipping:', messageId);
        return state;
      }

      // Add new message and enforce size limit
      const updatedQueue = [...validMessages, timestampedMessage].slice(-MAX_QUEUE_SIZE);

      return {
        lastMessage: message,
        messageQueue: updatedQueue,
        reconnectionState: {
          ...state.reconnectionState,
          lastMessageId: messageId,
        },
      };
    });

    // Notify subscribers
    const { subscriptions } = get();
    const channel = message.channel || message.type;

    // Notify channel-specific subscribers
    if (subscriptions.has(channel)) {
      subscriptions.get(channel)?.forEach((callback) => {
        try {
          callback(message);
        } catch (error) {
          logger.error('Error in subscription callback:', error);
        }
      });
    }

    // Notify wildcard subscribers (subscribed to '*')
    if (subscriptions.has('*')) {
      subscriptions.get('*')?.forEach((callback) => {
        try {
          callback(message);
        } catch (error) {
          logger.error('Error in wildcard subscription callback:', error);
        }
      });
    }
  },

  subscribe: (channel, callback) => {
    const { subscriptions } = get();

    if (!subscriptions.has(channel)) {
      subscriptions.set(channel, new Set());
    }

    subscriptions.get(channel)!.add(callback);

    // Return unsubscribe function
    return () => {
      const { subscriptions: currentSubscriptions } = get();
      currentSubscriptions.get(channel)?.delete(callback);
      if (currentSubscriptions.get(channel)?.size === 0) {
        currentSubscriptions.delete(channel);
      }
    };
  },

  clearMessages: () => set({ messageQueue: [], lastMessage: null }),

  getMessages: () => {
    const now = Date.now();
    const { messageQueue } = get();
    // Return only non-expired messages
    return messageQueue
      .filter((item) => now - item.timestamp < MESSAGE_TTL_MS)
      .map((item) => item.message);
  },

  // Outgoing queue management
  queueOutgoingMessage: (message) => {
    const id = generateMessageId();
    const queuedMessage: QueuedOutgoingMessage = {
      id,
      message,
      timestamp: Date.now(),
      retryCount: 0,
    };

    set((state) => {
      // Enforce queue size limit - remove oldest messages if needed
      let updatedQueue = [...state.outgoingQueue, queuedMessage];
      if (updatedQueue.length > MAX_OUTGOING_QUEUE_SIZE) {
        const overflow = updatedQueue.length - MAX_OUTGOING_QUEUE_SIZE;
        updatedQueue = updatedQueue.slice(overflow);
        logger.warn(`Outgoing queue overflow, dropped ${overflow} oldest messages`);
      }
      return { outgoingQueue: updatedQueue };
    });

    return id;
  },

  getOutgoingQueue: () => {
    const now = Date.now();
    const { outgoingQueue } = get();
    // Return only non-expired outgoing messages
    return outgoingQueue.filter((item) => now - item.timestamp < MESSAGE_TTL_MS);
  },

  clearOutgoingQueue: () => set({ outgoingQueue: [] }),

  removeFromOutgoingQueue: (id) => {
    set((state) => ({
      outgoingQueue: state.outgoingQueue.filter((item) => item.id !== id),
    }));
  },

  markMessageSent: (id) => {
    set((state) => ({
      outgoingQueue: state.outgoingQueue.filter((item) => item.id !== id),
    }));
  },

  // Connection health
  updatePingTime: () => {
    set((state) => ({
      connectionHealth: {
        ...state.connectionHealth,
        lastPingTime: Date.now(),
      },
    }));
  },

  updatePongTime: () => {
    const now = Date.now();
    set((state) => {
      const { lastPingTime } = state.connectionHealth;
      const latency = lastPingTime ? now - lastPingTime : null;
      return {
        connectionHealth: {
          ...state.connectionHealth,
          lastPongTime: now,
          latency,
          missedPongs: 0,
          isHealthy: true,
        },
      };
    });
  },

  incrementMissedPongs: () => {
    set((state) => {
      const missedPongs = state.connectionHealth.missedPongs + 1;
      return {
        connectionHealth: {
          ...state.connectionHealth,
          missedPongs,
          isHealthy: missedPongs < 3, // Unhealthy after 3 missed pongs
        },
      };
    });
  },

  resetConnectionHealth: () => {
    set({ connectionHealth: { ...initialConnectionHealth } });
  },

  getConnectionHealth: () => get().connectionHealth,

  // Reconnection state
  updateReconnectionState: (updates) => {
    set((state) => {
      const newReconnectionState = { ...state.reconnectionState, ...updates };
      return {
        reconnectionState: newReconnectionState,
        statusMessage: getStatusMessage(state.status, newReconnectionState),
      };
    });
  },

  resetReconnectionState: () => {
    set((state) => ({
      reconnectionState: { ...initialReconnectionState },
      statusMessage: getStatusMessage(state.status, initialReconnectionState),
    }));
  },

  getReconnectionState: () => get().reconnectionState,

  // Message replay support
  getMessagesAfter: (messageId) => {
    const now = Date.now();
    const { messageQueue } = get();

    // Filter out expired messages
    const validMessages = messageQueue.filter(
      (item) => now - item.timestamp < MESSAGE_TTL_MS
    );

    if (!messageId) {
      // Return all valid messages if no messageId specified
      return validMessages.map((item) => item.message);
    }

    // Find the index of the message with the given ID
    const startIndex = validMessages.findIndex((item) => item.id === messageId);

    if (startIndex === -1) {
      // Message not found, return all valid messages
      logger.debug('Message ID not found for replay, returning all messages');
      return validMessages.map((item) => item.message);
    }

    // Return messages after the specified message (excluding it)
    return validMessages.slice(startIndex + 1).map((item) => item.message);
  },

  setLastMessageId: (id) => {
    set((state) => ({
      reconnectionState: {
        ...state.reconnectionState,
        lastMessageId: id,
      },
    }));
  },
}));

// Cleanup function to clear all subscriptions
export const cleanupRealtimeStore = () => {
  const state = useRealtimeStore.getState();
  state.subscriptions.clear();
  state.clearMessages();
  state.clearOutgoingQueue();
  state.resetConnectionHealth();
  state.resetReconnectionState();
  // Replace the subscriptions Map entirely to prevent stale callbacks firing after logout
  useRealtimeStore.setState({ subscriptions: new Map() });
};

// Export types for external use
export type { ConnectionHealth, ReconnectionState, QueuedOutgoingMessage };
